/**
 * Shared Auth Page Styling Utilities
 *
 * Theme-aware styling helpers for login and signup pages
 * Ensures consistent glassmorphic design across both light and dark themes
 */

import { Theme, alpha } from '@mui/material';
import designSystem from '@shared/utils/designSystem';

/**
 * Glassmorphic background styling that adapts to theme
 * Creates a semi-transparent card with backdrop blur effect
 */
export const getGlassmorphismBackground = (theme: Theme) => {
  const isLight = theme.palette.mode === 'light';

  return {
    backgroundColor: alpha(
      isLight ? '#ffffff' : theme.palette.background.paper,
      isLight ? 0.95 : 0.9
    ),
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)', // Safari support
    border: `1px solid ${alpha(theme.palette.divider, isLight ? 0.2 : 0.3)}`,
  };
};

/**
 * Page background gradient that adapts to theme
 * Light mode: Subtle gray gradient
 * Dark mode: Rich navy gradient
 */
export const getPageBackgroundGradient = (theme: Theme) => {
  const isLight = theme.palette.mode === 'light';

  if (isLight) {
    return `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`;
  } else {
    return `linear-gradient(135deg, ${theme.palette.grey[900]} 0%, ${theme.palette.grey[800]} 100%)`;
  }
};

/**
 * Radial gradient background patterns for visual interest
 * Creates subtle colored circles in the background
 */
export const getBackgroundPattern = (theme: Theme) => {
  const isLight = theme.palette.mode === 'light';
  const primaryAlpha = isLight ? 0.08 : 0.15;
  const secondaryAlpha = isLight ? 0.04 : 0.1;

  return `
    radial-gradient(circle at 20% 80%, ${alpha(theme.palette.primary.main, primaryAlpha)} 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.light, secondaryAlpha)} 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, ${alpha(theme.palette.info.main, secondaryAlpha)} 0%, transparent 50%)
  `;
};

/**
 * Back button styling with glassmorphic effect
 * Positioned absolutely in top-left corner
 */
export const getBackButtonStyles = (theme: Theme) => ({
  position: 'absolute' as const,
  top: { xs: designSystem.spacing.md, md: designSystem.spacing.lg },
  left: { xs: designSystem.spacing.md, md: designSystem.spacing.lg },
  color: theme.palette.text.secondary,
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)', // Safari support
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  boxShadow: designSystem.shadows.md,
  zIndex: 10,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: designSystem.shadows.lg,
  },
  transition: `all ${designSystem.transitions.duration.normal}ms ${designSystem.transitions.easing.easeInOut}`,
});

/**
 * TextField styling with theme-aware colors
 * Consistent input field appearance across auth pages
 */
export const getTextFieldStyles = (theme: Theme) => {
  const isLight = theme.palette.mode === 'light';

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: `${designSystem.borderRadius.sm}px`,
      backgroundColor: alpha(
        isLight ? theme.palette.background.default : theme.palette.grey[700],
        isLight ? 0.8 : 1
      ),
      transition: `all ${designSystem.transitions.duration.normal}ms ${designSystem.transitions.easing.easeInOut}`,

      '& fieldset': {
        border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
        transition: `all ${designSystem.transitions.duration.normal}ms ${designSystem.transitions.easing.easeInOut}`,
      },

      '&:hover fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.5),
      },

      '&.Mui-focused': {
        backgroundColor: isLight ? '#ffffff' : theme.palette.grey[700],
        boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,

        '& fieldset': {
          borderColor: theme.palette.primary.main,
          borderWidth: '2px',
        },
      },

      '&.Mui-error fieldset': {
        borderColor: theme.palette.error.main,
      },
    },

    '& .MuiInputBase-input': {
      padding: `${designSystem.spacing.sm + 4}px ${designSystem.spacing.md}px`,
      fontSize: designSystem.typography.fontSize.base,
      fontWeight: designSystem.typography.fontWeight.medium,
      color: theme.palette.text.primary,
      fontFamily: designSystem.typography.fontFamily.base,

      '&::placeholder': {
        color: alpha(theme.palette.text.secondary, 0.6),
        opacity: 1,
      },
    },

    '& .MuiInputLabel-root': {
      fontSize: designSystem.typography.fontSize.base,
      fontWeight: designSystem.typography.fontWeight.medium,
      color: theme.palette.text.secondary,
      fontFamily: designSystem.typography.fontFamily.base,

      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },

      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },

    '& .MuiFormHelperText-root': {
      fontSize: designSystem.typography.fontSize.sm,
      fontFamily: designSystem.typography.fontFamily.base,
      marginLeft: `${designSystem.spacing.xs}px`,

      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },
  };
};
